import { isNativePlatform } from "@/native/platform";
import { useEffect, useState } from "react";

/**
 * Is this a mobile device — a touch-first surface with no fine pointer —
 * as opposed to a narrow window? The canvas is read-only on mobile, and
 * that is a capability of the device, not of its width: a desktop window
 * dragged to 900px still has a mouse and a keyboard and can author, while
 * an iPad in landscape is 1024+ CSS px and cannot.
 *
 * Signals, in order:
 *
 * - `isNativePlatform()` — the Capacitor container, `VITE_FORCE_NATIVE`, or
 *   the `?forceNative` dev switch. The app is on a phone by construction.
 * - `(hover: none) and (pointer: coarse)` — the *primary* input cannot hover
 *   and is imprecise: a finger. This is what distinguishes an iPad from a
 *   Mac when Safari presents the iPad with a Mac user agent, which it does
 *   by default on iPadOS 13+; no user-agent string is consulted here.
 * - `not (any-pointer: fine)` and `not (any-hover: hover)` — no *secondary*
 *   input is precise or can hover. An iPad with a trackpad or mouse
 *   attached, or a touch laptop, reports a fine pointer somewhere and is
 *   not mobile: it can author.
 *
 * `navigator.maxTouchPoints` is only consulted where the media queries do
 * not exist (SSR, a bare test DOM); it never outranks them, because a
 * touch-screen laptop has touch points and a mouse.
 *
 * Exported as a plain predicate too, so a permission gate can read it
 * without a hook.
 */

const PRIMARY_IS_TOUCH = "(hover: none) and (pointer: coarse)";
const ANY_FINE_POINTER = "(any-pointer: fine)";
const ANY_HOVER = "(any-hover: hover)";

export function isMobileDevice(): boolean {
  if (isNativePlatform()) return true;
  if (typeof window === "undefined") return false;
  if (typeof window.matchMedia !== "function") {
    return (navigator.maxTouchPoints ?? 0) > 1;
  }
  return (
    window.matchMedia(PRIMARY_IS_TOUCH).matches &&
    !window.matchMedia(ANY_FINE_POINTER).matches &&
    !window.matchMedia(ANY_HOVER).matches
  );
}

export function useIsMobileDevice(): boolean {
  const [mobile, setMobile] = useState(isMobileDevice);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const queries = [PRIMARY_IS_TOUCH, ANY_FINE_POINTER, ANY_HOVER].map((q) =>
      window.matchMedia(q),
    );
    const sync = () => setMobile(isMobileDevice());
    sync();
    // Attaching a trackpad to an iPad flips any-pointer live.
    queries.forEach((q) => q.addEventListener("change", sync));
    return () => queries.forEach((q) => q.removeEventListener("change", sync));
  }, []);

  return mobile;
}
